<template>
  <div class="min-h-screen bg-gray-50 p-6">
    <div class="max-w-4xl mx-auto">
      <div class="bg-white rounded-lg shadow-md p-6 mb-6">
        <h1 class="text-2xl font-bold text-gray-900 mb-4">AI Functionality Test</h1>
        
        <!-- Status Section -->
        <div class="mb-6">
          <h2 class="text-lg font-semibold text-gray-800 mb-3">System Status</h2>
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div class="bg-blue-50 p-4 rounded-lg">
              <h3 class="font-medium text-blue-900">AI Service</h3>
              <p class="text-blue-700">{{ aiStatus }}</p>
            </div>
            <div class="bg-green-50 p-4 rounded-lg">
              <h3 class="font-medium text-green-900">Available Models</h3>
              <p class="text-green-700">{{ availableModels.length }} models</p>
            </div>
            <div class="bg-purple-50 p-4 rounded-lg">
              <h3 class="font-medium text-purple-900">API Connection</h3>
              <p class="text-purple-700">{{ apiStatus }}</p>
            </div>
          </div>
        </div>

        <!-- Model Selection -->
        <div class="mb-6">
          <h2 class="text-lg font-semibold text-gray-800 mb-3">Test AI Models</h2>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div v-for="model in availableModels" :key="model.id" class="border rounded-lg p-4">
              <h3 class="font-medium text-gray-900">{{ model.name }}</h3>
              <p class="text-sm text-gray-600 mb-2">{{ model.provider }} - {{ model.model }}</p>
              <button 
                @click="testModel(model.id)"
                :disabled="isTesting"
                class="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {{ isTesting ? 'Testing...' : 'Test Model' }}
              </button>
            </div>
          </div>
        </div>

        <!-- Test Results -->
        <div v-if="testResults.length > 0" class="mb-6">
          <h2 class="text-lg font-semibold text-gray-800 mb-3">Test Results</h2>
          <div class="space-y-4">
            <div v-for="result in testResults" :key="result.id" class="border rounded-lg p-4">
              <div class="flex justify-between items-start mb-2">
                <h3 class="font-medium text-gray-900">{{ result.model }}</h3>
                <span :class="result.success ? 'text-green-600' : 'text-red-600'" class="text-sm font-medium">
                  {{ result.success ? 'Success' : 'Failed' }}
                </span>
              </div>
              <p class="text-sm text-gray-600 mb-2">{{ result.prompt }}</p>
              <div class="bg-gray-50 p-3 rounded">
                <p class="text-sm text-gray-800">{{ result.response }}</p>
              </div>
              <p class="text-xs text-gray-500 mt-2">Response time: {{ result.responseTime }}ms</p>
            </div>
          </div>
        </div>

        <!-- Quick Test -->
        <div class="mb-6">
          <h2 class="text-lg font-semibold text-gray-800 mb-3">Quick Test</h2>
          <div class="flex gap-4 mb-4">
            <select v-model="selectedModel" class="border rounded px-3 py-2">
              <option value="">Select Model</option>
              <option v-for="model in availableModels" :key="model.id" :value="model.id">
                {{ model.name }}
              </option>
            </select>
            <button 
              @click="quickTest"
              :disabled="!selectedModel || isTesting"
              class="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:opacity-50"
            >
              {{ isTesting ? 'Testing...' : 'Quick Test' }}
            </button>
          </div>
          <div v-if="quickTestResult" class="bg-gray-50 p-4 rounded">
            <h3 class="font-medium text-gray-900 mb-2">Response:</h3>
            <p class="text-gray-800">{{ quickTestResult }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'

// Reactive state
const aiStatus = ref('Initializing...')
const apiStatus = ref('Checking...')
const availableModels = ref<any[]>([])
const isTesting = ref(false)
const testResults = ref<any[]>([])
const selectedModel = ref('')
const quickTestResult = ref('')

// Initialize AI service
const initializeAI = async () => {
  try {
    aiStatus.value = 'Initializing...'
    
    // Try to import AI service dynamically
    const { aiService, AI_MODELS } = await import('@/services/aiService')
    
    await aiService.initialize()
    aiStatus.value = 'Ready'
    
    // Load available models
    const models = await aiService.getAvailableModels()
    availableModels.value = models
    apiStatus.value = 'Connected'
  } catch (error) {
    console.error('AI initialization failed:', error)
    aiStatus.value = 'Failed'
    apiStatus.value = 'Error'
    // Fallback to local models
    availableModels.value = [
      {
        id: 'claude-sonnet-4',
        name: 'Claude Sonnet 4',
        provider: 'anthropic',
        model: 'claude-3-5-sonnet-20241022'
      },
      {
        id: 'gpt-5',
        name: 'GPT-5',
        provider: 'openai',
        model: 'gpt-5'
      }
    ]
  }
}

// Test a specific model
const testModel = async (modelId: string) => {
  if (isTesting.value) return
  
  isTesting.value = true
  const startTime = Date.now()
  
  try {
    const { aiService } = await import('@/services/aiService')
    
    const testPrompt = "You are an oilfield expert. Provide a brief overview of safety considerations for drilling operations."
    
    const response = await aiService.sendMessage({
      agentId: 'test-agent',
      message: testPrompt,
      model: modelId,
      context: 'safety'
    })
    
    const responseTime = Date.now() - startTime
    
    testResults.value.unshift({
      id: Date.now(),
      model: modelId,
      prompt: testPrompt,
      response: response.content,
      success: true,
      responseTime
    })
  } catch (error) {
    console.error('Model test failed:', error)
    const responseTime = Date.now() - startTime
    
    testResults.value.unshift({
      id: Date.now(),
      model: modelId,
      prompt: 'Test prompt',
      response: error.message || 'Failed to get response',
      success: false,
      responseTime
    })
  } finally {
    isTesting.value = false
  }
}

// Quick test with selected model
const quickTest = async () => {
  if (!selectedModel.value || isTesting.value) return
  
  isTesting.value = true
  quickTestResult.value = ''
  
  try {
    const { aiService } = await import('@/services/aiService')
    
    const response = await aiService.sendMessage({
      agentId: 'quick-test',
      message: "Hello! Can you confirm you're working properly?",
      model: selectedModel.value,
      context: 'general'
    })
    
    quickTestResult.value = response.content
  } catch (error) {
    console.error('Quick test failed:', error)
    quickTestResult.value = `Error: ${error.message || 'Failed to get response'}`
  } finally {
    isTesting.value = false
  }
}

// Initialize on mount
onMounted(() => {
  initializeAI()
})
</script>
